import { Avatar, Box, Group, Stack, Text, Title } from '@mantine/core';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { IllustrativeNotice, UnderlineTabs } from '~portal/components/common';
import { memberDisplayName } from '~portal/data/account';
import { useAccount } from '~portal/data/hooks';
import { isStandalone, logger } from '~portal/lib';
import { AccountTab } from './-account/AccountTab';
import { CompanyTab } from './-account/CompanyTab';
import { PlanTab } from './-account/PlanTab';
import { type AccountTab as TabId, parseAccountSearch } from './-account/search';
import { serifEyebrow } from './-home/home.css';

/** @desc Figma's tab labels (`295:53567`, A2). */
const ACCOUNT_TABS: ReadonlyArray<{ value: TabId; label: string }> = [
  { value: 'account', label: 'Account Details' },
  { value: 'company', label: 'Company Details' },
  { value: 'plan', label: 'Plan Details' },
];

/**
 * @desc The account page (Figma `295:53567`): a profile masthead — no page title — over the
 * underline tabs. The active tab lives in the URL (`?tab=`, default omitted) so a tab can be
 * linked to. Platform-only: the standalone build is the chat app alone, so it redirects to a new
 * chat.
 */
export const Route = createFileRoute('/_protected/account')({
  validateSearch: parseAccountSearch,
  beforeLoad: () => {
    if (isStandalone) {
      throw redirect({ to: '/chat/$conversationId', params: { conversationId: 'new' } });
    }
  },
  component: AccountPage,
});

function AccountPage() {
  const { tab = 'account' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const select = (value: TabId) => {
    const next = parseAccountSearch({ tab: value }).tab;
    navigate({
      search: next ? { tab: next } : {},
      // A tab is an in-place control: the tab strip stays where the reader is.
      resetScroll: false,
    }).catch((err) => {
      logger.error('[Account] Navigation failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  return (
    <Stack gap="lg" p="lg">
      <ProfileMasthead />
      <IllustrativeNotice>
        Company, members and plans are placeholders for design review; prices, seats and features
        are pending tiering. Edits stay on this page — nothing saves yet.
      </IllustrativeNotice>
      <UnderlineTabs
        tabs={ACCOUNT_TABS}
        value={tab}
        onChange={select}
        aria-label="Account sections"
        keepMounted={false}
      >
        <UnderlineTabs.Panel value="account" pt="lg">
          <AccountTab />
        </UnderlineTabs.Panel>
        <UnderlineTabs.Panel value="company" pt="lg">
          <CompanyTab />
        </UnderlineTabs.Panel>
        <UnderlineTabs.Panel value="plan" pt="lg">
          <PlanTab />
        </UnderlineTabs.Panel>
      </UnderlineTabs>
    </Stack>
  );
}

/**
 * @desc The profile masthead (A1): a 96px initials tile at the 16px radius (no photos — client
 * decision), the company as a serif eyebrow, the name as the page's `h1` and the job title. Name
 * and company come from the signed-in user; the job title is the fixture's until WorkOS carries one.
 */
function ProfileMasthead() {
  const { data: account } = useAccount();
  const { profile, company } = account;
  const fullName = memberDisplayName(profile);
  return (
    <Group gap="lg" wrap="nowrap" align="center" data-testid="account-masthead">
      <Avatar size={96} radius="lg" name={fullName} color="initials" />
      <Box miw={0}>
        <Text className={serifEyebrow}>{company.name}</Text>
        <Title order={1}>{fullName}</Title>
        <Text c="dimmed" mt={4}>
          {profile.jobTitle}
        </Text>
      </Box>
    </Group>
  );
}
